import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { useAuth } from "../../auth/useAuth";
import { errorMessageFor } from "../../api/client";
import { landingRouteFor } from "../../app/navigation";

// Both fields are required and nothing more. The username is NOT validated as
// an email: a login id without an @ must still submit.
const schema = z.object({
  username: z.string().min(1, "This field is required"),
  password: z.string().min(1, "This field is required"),
  rememberMe: z.boolean(),
});

type LoginForm = z.infer<typeof schema>;

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [failure, setFailure] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    // The button stays disabled until the form is valid, so validation has to
    // run as the user types rather than on submit.
    mode: "onChange",
    defaultValues: { username: "", password: "", rememberMe: false },
  });

  const onSubmit = handleSubmit(async ({ username, password, rememberMe }) => {
    setFailure(null);
    try {
      const principal = await signIn(username, password, rememberMe);
      // `?url=` from RequireSession wins; otherwise fall through the
      // authority ladder.
      const requested = params.get("url");
      navigate(requested ?? landingRouteFor(principal.authorities), { replace: true });
    } catch (error) {
      setFailure(errorMessageFor(error) ?? "Sign in failed. Check your email and password.");
    }
  });

  return (
    <Box sx={{ display: "grid", placeItems: "center", minHeight: "100vh", p: 2 }}>
      <Paper sx={{ p: 4, width: "100%", maxWidth: 400 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Log in
        </Typography>

        {failure && (
          <Alert severity="error" sx={{ mb: 2 }} role="alert">
            {failure}
          </Alert>
        )}

        <Box component="form" onSubmit={onSubmit} noValidate>
          <TextField
            {...register("username")}
            label="Email"
            type="text"
            fullWidth
            margin="normal"
            autoComplete="username"
            error={Boolean(errors.username)}
            helperText={errors.username?.message}
          />
          <TextField
            {...register("password")}
            label="Password"
            type="password"
            fullWidth
            margin="normal"
            autoComplete="current-password"
            error={Boolean(errors.password)}
            helperText={errors.password?.message}
          />
          <FormControlLabel
            control={<Checkbox {...register("rememberMe")} color="primary" />}
            label="Remember me"
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={!isValid || isSubmitting}
            sx={{ mt: 2 }}
          >
            {isSubmitting ? "loading..." : "Login"}
          </Button>
        </Box>

        <Typography sx={{ mt: 2 }}>
          <Link to="/forget-password">Forgot Password ?</Link>
        </Typography>
      </Paper>
    </Box>
  );
}
